// /src/app/modules/cuentas/components/ConceptosCuentaModal.tsx
import React, {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'

type Concepto = {
  id: number
  nombre: string
  valor: number
  descripcion: string
  recurrente: boolean
  variable: boolean
}

type CuentaInfo = {
  numero: number
  conceptos: Concepto[]
}

interface ConceptosCuentaModalProps {
  isOpen: boolean
  onClose: () => void
  cuenta: CuentaInfo
  onApply: (conceptosActualizados: Concepto[]) => void
  isSaving?: boolean
}

type ConceptoWithIncluded = Concepto & { included: boolean, valorEditado?: string }

const ConceptosCuentaModal: React.FC<ConceptosCuentaModalProps> = ({isOpen, onClose, cuenta, onApply, isSaving = false}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [conceptos, setConceptos] = useState<ConceptoWithIncluded[]>([])
  const [filter, setFilter] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const fetchConceptos = async (pageToLoad: number) => {
    try {
      setLoading(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getConceptos',
          current_page: pageToLoad,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)

      const data = await response.json()
      if (data.code !== 'OK') throw new Error(data.returned_message || 'Error en la respuesta')

      const incluidos = new Set((cuenta?.conceptos || []).map((c) => c.id))
      const valorPorId = new Map<number, number>(
        (cuenta?.conceptos || []).map((cc) => [
          cc.id,
          typeof cc.valor === 'number' ? cc.valor : parseFloat(String(cc.valor)) || 0,
        ])
      )

      const conceptosConFlag: ConceptoWithIncluded[] = (data.data.conceptos || []).map((c: Concepto) => {
        const incluido = incluidos.has(c.id)
        const valorCuenta = valorPorId.get(c.id)
        const valorNumerico = typeof c.valor === 'number' ? c.valor : parseFloat(String(c.valor)) || 0
        // Usar valor de la cuenta cuando esté adjudicado, sino el valor por defecto del concepto
        const valorInicial =
          incluido && valorCuenta !== undefined && !isNaN(valorCuenta) ? String(valorCuenta) : String(valorNumerico)
        return {
          ...c,
          valor: valorNumerico,
          included: incluido,
          valorEditado: valorInicial,
        }
      })

      // Ordenar: primero incluidos
      conceptosConFlag.sort((a, b) => Number(b.included) - Number(a.included))

      setConceptos(conceptosConFlag)
      setTotalPages(data.data.totalPages || 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setPage(1)
      fetchConceptos(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cuenta?.numero])

  const filteredConceptos = useMemo(() => {
    if (!filter) return conceptos
    const f = filter.toLowerCase()
    return conceptos.filter((c) => c.nombre.toLowerCase().includes(f))
  }, [filter, conceptos])

  const isProtectedConcept = (c: ConceptoWithIncluded): boolean => {
    // "Cuota Base" no se puede eliminar
    const name = (c.nombre || '').trim().toLowerCase()
    return c.id === 1 || name === 'cuota base'
  }

  const toggleConcepto = (id: number) => {
    setConceptos((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c
        if (c.included && isProtectedConcept(c)) return c
        return {...c, included: !c.included}
      })
    )
  }

  const handleValorChange = (id: number, nuevoValor: string) => {
    // Permitir solo números válidos (vacío, enteros o decimales con punto/coma)
    const normalizado = nuevoValor.replace(',', '.')
    if (/^\d*(\.?\d*)?$/.test(normalizado)) {
      setConceptos((prev) =>
        prev.map((c) => (c.id === id ? {...c, valorEditado: normalizado} : c))
      )
    }
  }

  const handleApply = () => {
    const seleccionados = conceptos
      .filter((c) => c.included)
      .map((c) => ({
        id: c.id,
        nombre: c.nombre,
        valor: parseFloat(c.valorEditado || '0') || 0,
        descripcion: c.descripcion,
        recurrente: c.recurrente,
        variable: c.variable,
      }))
    onApply(seleccionados)
  }

  // Control de scroll del body
  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.classList.add('modal-open')
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.classList.remove('modal-open')
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div className='modal fade show d-block' tabIndex={-1} role='dialog' aria-modal='true' style={{zIndex: 2000, position: 'fixed', inset: 0}}>
      <div className='modal-dialog modal-lg modal-dialog-scrollable' role='document' style={{position: 'relative', zIndex: 2001}}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <i className='ki-duotone ki-tag fs-2 me-2 text-primary'>
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
              </i>
              Editar conceptos - Cuenta #{cuenta?.numero}
            </h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
          </div>
          <div className='modal-body'>
            <div className='alert alert-info d-flex align-items-center mb-5'>
              <i className='ki-duotone ki-information-5 fs-2x me-3'>
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
              </i>
              <div>
                <strong>Nota:</strong> Los conceptos marcados como "Recurrentes" se aplicarán automáticamente en cada facturación mensual. 
                Los conceptos "Variables" permiten modificar el monto.
              </div>
            </div>

            <div className='d-flex gap-3 mb-4 align-items-end'>
              <div className='flex-grow-1'>
                <label className='form-label fw-bold'>Buscar concepto</label>
                <input
                  type='text'
                  className='form-control form-control-lg'
                  placeholder='Nombre del concepto...'
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              <div>
                <button
                  className='btn btn-light-primary btn-lg'
                  onClick={() => fetchConceptos(page)}
                  disabled={loading}
                >
                  <i className='ki-duotone ki-arrows-circle fs-3'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </button>
              </div>
            </div>

            {loading && (
              <div className='text-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Cargando...</span>
                </div>
              </div>
            )}

            {error && (
              <div className='alert alert-danger d-flex align-items-center'>
                <i className='ki-duotone ki-information-5 fs-2x text-danger me-3'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                </i>
                <div>{error}</div>
              </div>
            )}

            {!loading && !error && (
              <>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted bg-light'>
                        <th style={{width: '60px'}}>ID</th>
                        <th>Nombre</th>
                        <th style={{width: '140px'}}>Valor</th>
                        <th style={{width: '120px'}}>Tipo</th>
                        <th style={{width: '140px'}} className='text-center'>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredConceptos.map((c) => (
                        <tr key={c.id} className={c.included ? 'bg-light-success' : ''}>
                          <td className='fw-bold'>{c.id}</td>
                          <td>
                            <div className='d-flex flex-column'>
                              <span className='fw-bold'>{c.nombre}</span>
                              {c.descripcion && (
                                <span className='text-muted fs-7'>{c.descripcion}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {c.included ? (
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={c.valorEditado ?? ''}
                                onChange={(e) => handleValorChange(c.id, e.target.value)}
                                placeholder='Monto'
                              />
                            ) : (
                              <span className='fw-bold'>${(typeof c.valor === 'number' ? c.valor : parseFloat(String(c.valor)) || 0).toFixed(2)}</span>
                            )}
                          </td>
                          <td>
                            <div className='d-flex flex-column gap-1'>
                              {c.recurrente && (
                                <span className='badge badge-light-primary'>Recurrente</span>
                              )}
                              {c.variable && (
                                <span className='badge badge-light-warning'>Variable</span>
                              )}
                            </div>
                          </td>
                          <td className='text-center'>
                            {c.included ? (
                              <button
                                className='btn btn-sm btn-danger'
                                onClick={() => toggleConcepto(c.id)}
                                disabled={isProtectedConcept(c)}
                                title={isProtectedConcept(c) ? 'No se puede quitar CUOTA BASE' : 'Quitar concepto'}
                              >
                                <i className='ki-duotone ki-minus-circle fs-3'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                                Quitar
                              </button>
                            ) : (
                              <button 
                                className='btn btn-sm btn-success' 
                                onClick={() => toggleConcepto(c.id)}
                                title='Agregar concepto'
                              >
                                <i className='ki-duotone ki-plus-circle fs-3'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                                Agregar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center mt-4'>
                    <button
                      className='btn btn-sm btn-secondary'
                      onClick={() => {
                        const next = Math.max(1, page - 1)
                        setPage(next)
                        fetchConceptos(next)
                      }}
                      disabled={page === 1 || loading}
                    >
                      <i className='ki-duotone ki-arrow-left fs-3'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>
                      Anterior
                    </button>
                    <span className='fw-semibold'>Página {page} de {totalPages}</span>
                    <button
                      className='btn btn-sm btn-secondary'
                      onClick={() => {
                        const next = Math.min(totalPages, page + 1)
                        setPage(next)
                        fetchConceptos(next)
                      }}
                      disabled={page === totalPages || loading}
                    >
                      Siguiente
                      <i className='ki-duotone ki-arrow-right fs-3'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>
                    </button>
                  </div>
                )}

                {/* Resumen de selección */}
                <div className='mt-6 p-4 bg-light-primary rounded'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      <span className='fw-bold text-gray-800'>Conceptos seleccionados: </span>
                      <span className='badge badge-primary fs-6'>{conceptos.filter(c => c.included).length}</span>
                    </div>
                    <div>
                      <span className='fw-bold text-gray-800'>Total: </span>
                      <span className='text-primary fs-4 fw-bold'>
                        ${conceptos
                          .filter((c) => c.included)
                          .reduce((acc, c) => acc + (parseFloat(c.valorEditado || '0') || 0), 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className='modal-footer'>
            <button className='btn btn-light' onClick={onClose}>
              <i className='ki-duotone ki-cross fs-3 me-1'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              Cancelar
            </button>
            <button className='btn btn-primary' onClick={handleApply} disabled={loading || isSaving}>
              {isSaving ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' />
                  Guardando...
                </>
              ) : (
                <>
                  <i className='ki-duotone ki-check fs-3 me-1'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                  Confirmar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' style={{zIndex: 1990, pointerEvents: 'none'}} />
    </div>,
    document.body
  )
}

export default ConceptosCuentaModal

